import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { Plus, Search, Pencil, Trash2, Copy } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import CreateTemplateModal from '@/components/Modals/CreateTemplateModal';
import EditTemplateModal from '@/components/Modals/EditTemplateModal';
import { toast } from 'react-toastify';
import api from '@/lib/api';

const TemplatePage = () => {
    const [templateSearchTerm, setTemplateSearchTerm] = useState('');
    const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
    const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);
    const [newTemplate, setNewTemplate] = useState({
        title: '',
        subject: '',
        scenario: '',
        tone: '',
        content: '',
    });
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [scenarios, setScenarios] = useState([]);

    useEffect(() => {
        api.get('/api/campaigns/scenarios/')
            .then((res) => {
                setScenarios(res.data);
            })
            .catch((err) => {
                console.error('❌ Не удалось загрузить сценарии', err);
                setScenarios([]);
            });
    }, []);

    const fetchTemplates = () => {
        api
            .get('/api/templates/')
            .then((res) => {
                const data =
                    Array.isArray(res.data) ? res.data :
                        Array.isArray(res.data.results) ? res.data.results : [];

                // сортировка: новые/обновлённые — вперёд
                const sorted = [...data].sort((a, b) => {
                    const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
                    const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
                    return dateB - dateA; // новые сверху
                });

                setTemplates(sorted);
            })
            .catch((err) => {
                console.error('Failed to load templates', err);
                setTemplates([]);
            });
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    const handleCreateTemplate = (templateData) => {
        api
            .post('/api/templates/', templateData)
            .then(() => {
                fetchTemplates();
                setShowCreateTemplateModal(false);
            })
            .catch((err) => console.error('Failed to create template', err));
    };

    const handleDuplicateTemplate = async (template) => {
        const { title, subject, content, html, scenario, tone, step_number } = template;

        const duplicate = {
            title: `${title} (Copy)`,
            subject,
            content,
            html,
            scenario,
            tone,
            step_number, // если используете
        };

        try {
            await api.post('/api/templates/', duplicate);
            fetchTemplates();
            toast.success('Template duplicated!');
        } catch (err) {
            console.error('Failed to duplicate template', err);
            toast.error('Duplicate failed');
        }
    };

    const handleUpdateTemplate = async (templateData) => {
        try {
            await api.put(`/api/templates/${editingTemplate.id}/`, templateData);
            fetchTemplates();
            toast.success("Template updated"); // если используешь shadcn или react-toastify
        } catch (err) {
            console.error('Failed to update template', err);
            toast.error("Update failed");
        } finally {
            setShowEditTemplateModal(false);
            setEditingTemplate(null);
        }
    };

    const handleDeleteTemplate = (id) => {
        api
            .delete(`/api/templates/${id}/`)
            .then(() => fetchTemplates())
            .catch((err) => console.error('Failed to delete template', err));
    };

    const filteredTemplates = Array.isArray(templates)
        ? templates.filter((template) =>
            template.title.toLowerCase().includes(templateSearchTerm.toLowerCase())
        )
        : [];

    const getScenarioLabel = (value, scenarios) => {
        const found = scenarios.find(s => String(s.value) === String(value));
        return found ? found.label : value; // fallback — показать код, если не нашли
    };
    return (
        <div className="space-y-6">
            <h2 className="text-3xl font-bold flex items-center justify-between">
                Email Templates
                <Button onClick={() => setShowCreateTemplateModal(true)}>
                    <Plus className="mr-2 h-4 w-4" /> Create New Template
                </Button>
            </h2>

            <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    placeholder="Search templates..."
                    className="pl-9"
                    value={templateSearchTerm}
                    onChange={(e) => setTemplateSearchTerm(e.target.value)}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTemplates.length === 0 ? (
                    <p className="text-center text-muted-foreground col-span-full">
                        No templates found.
                    </p>
                ) : (
                    filteredTemplates.map((template) => (
                        <Card key={template.id} className="relative">
                            <div className="absolute top-3 right-3 flex gap-2">
                                <Copy
                                    className="h-4 w-4 cursor-pointer text-muted-foreground"
                                    onClick={() => handleDuplicateTemplate(template)}
                                />
                                <Pencil
                                    className="h-4 w-4 cursor-pointer text-muted-foreground"
                                    onClick={() => {
                                        setEditingTemplate(template);
                                        setShowEditTemplateModal(true);
                                    }}
                                />
                                <Trash2
                                    className="h-4 w-4 cursor-pointer text-red-500"
                                    onClick={() => handleDeleteTemplate(template.id)}
                                />
                            </div>
                            <CardHeader className="mt-4">
                                <CardTitle>{template.title}</CardTitle>
                                <CardDescription>Template scenario: {getScenarioLabel(template.scenario, scenarios)}</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <p className="text-sm font-medium">Template Subject: {template.subject}</p>
                                <p className="text-sm font-medium">Template Step Number: {template.step_number}</p>
                                {/* <div
                                    className="text-sm text-muted-foreground line-clamp-3"
                                    dangerouslySetInnerHTML={{ __html: template.html }} // ✅
                                ></div> */}
                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                    <Badge variant="outline">{template.tone}</Badge>
                                    <span>
                                        Last Modified:{' '}
                                        {new Date(template.updated_at).toLocaleString()}
                                    </span>
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>

            <CreateTemplateModal
                isOpen={showCreateTemplateModal}
                onClose={() => setShowCreateTemplateModal(false)}
                newTemplate={newTemplate}
                setNewTemplate={setNewTemplate}
                scenarios={scenarios}
                onCreateTemplate={handleCreateTemplate}
            />

            {editingTemplate && (
                <EditTemplateModal
                    isOpen={showEditTemplateModal}
                    onClose={() => setShowEditTemplateModal(false)}
                    template={editingTemplate}
                    setTemplate={setEditingTemplate} // обязательно передай, чтобы работал input
                    onSaveTemplate={handleUpdateTemplate} // ✅ правильный проп
                    scenarios={scenarios}
                />
            )}
        </div>
    );
};

export default TemplatePage;